import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams, Link } from 'react-router-dom';
import { useAuth, API } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import AppEditor from '../components/SDSEditorApp';
import { initialSDSData, buildInitialSDSData, STORAGE_KEY } from '../data/initialData';
import { ArrowRight, Save, Loader2, Eye, Home } from 'lucide-react';
import { toast } from 'sonner';

// Wrap the existing AppEditor (which uses localStorage) and add Save-to-backend.
// Strategy: on mount, pre-load localStorage with the SDS data from backend (or new defaults),
// then render AppEditor. Save button reads from localStorage and POSTs to backend.
export default function SDSEditorPage() {
  const { t, i18n } = useTranslation();
  const isEn = i18n.language === 'en';
  const dir = isEn ? 'ltr' : 'rtl';
  const { user } = useAuth();
  const nav = useNavigate();
  const { id } = useParams();
  const [search] = useSearchParams();
  const autoPreview = search.get('preview') === '1';
  const isNew = !id || id === 'new';
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [title, setTitle] = useState('');
  const loadedRef = useRef(false);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        if (isNew) {
          // Pre-fill facility from user — use language-aware defaults so the report content
          // matches the currently selected UI language.
          const base = buildInitialSDSData(t);
          base.facility = {
            name: user?.factoryName || '',
            activity: user?.activity || '',
            location: user?.city || '',
          };
          localStorage.setItem(STORAGE_KEY, JSON.stringify(base));
          setTitle(t('factory.newReport'));
        } else {
          const r = await axios.get(`${API}/sds/${id}`);
          if (cancel) return;
          const data = r.data?.data || initialSDSData;
          localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
          setTitle(r.data?.title || t('factory.newReport'));
        }
        loadedRef.current = true;
        // Auto-trigger PDF preview if ?preview=1
        if (autoPreview && !cancel) {
          setTimeout(() => {
            window.dispatchEvent(new CustomEvent('sds:preview-pdf'));
          }, 1200); // give template a moment to render
        }
      } catch (e) {
        toast.error(t('sds.previewError'));
        nav('/factory');
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isNew]);

  const captureAndUploadThumbnail = async (sdsId) => {
    try {
      if (typeof window.__sdsCapture !== 'function') return;
      // Wait a tick to ensure template is fully rendered
      await new Promise((r) => setTimeout(r, 400));
      const dataUri = await window.__sdsCapture({ pixelRatio: 0.6 });
      if (!dataUri) return;
      await axios.post(`${API}/sds/${sdsId}/thumbnail`, { thumbnail: dataUri });
    } catch (e) {
      console.warn('thumbnail capture failed', e);
    }
  };

  const buildAndUploadPdf = async (sdsId) => {
    try {
      if (typeof window.__sdsBuildPdfBlob !== 'function') return;
      await new Promise((r) => setTimeout(r, 400));
      const blob = await window.__sdsBuildPdfBlob();
      if (!blob) return;
      const fd = new FormData();
      fd.append('file', blob, 'sds.pdf');
      await axios.post(`${API}/sds/${sdsId}/pdf`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    } catch (e) {
      console.warn('pdf upload failed', e);
    }
  };

  const saveToBackend = async (afterPreview = false) => {
    setSaving(true);
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      const data = raw ? JSON.parse(raw) : initialSDSData;
      const payload = {
        title: title || data?.facility?.name || t('factory.newReport'),
        data,
      };
      let savedId = id;
      if (isNew) {
        const r = await axios.post(`${API}/sds`, payload);
        savedId = r.data.id;
        toast.success(isEn ? 'Report saved' : 'تم حفظ التقرير');
        if (afterPreview) window.dispatchEvent(new CustomEvent('sds:preview-pdf'));
        nav(`/factory/edit/${savedId}`, { replace: true });
      } else {
        await axios.put(`${API}/sds/${id}`, payload);
        toast.success(isEn ? 'Report updated' : 'تم تحديث التقرير');
        if (afterPreview) window.dispatchEvent(new CustomEvent('sds:preview-pdf'));
      }
      // Generate thumbnail + PDF in background (non-blocking)
      if (savedId) {
        captureAndUploadThumbnail(savedId);
        buildAndUploadPdf(savedId);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || (isEn ? 'Unable to save' : 'تعذر الحفظ'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" dir={dir}>
        <Loader2 className="animate-spin text-[#1a4d80]" size={32} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100" dir={dir}>
      {/* Sticky top bar with Back + Save */}
      <div className="no-print sticky top-0 z-40 bg-[#1a4d80] text-white px-4 py-2 flex items-center justify-between shadow">
        <div className="flex items-center gap-2">
          {/* Navigation cluster: Back to previous page + Home shortcut */}
          <div className="flex items-center bg-white/10 rounded-md overflow-hidden border border-white/20" data-testid="sds-editor-nav-cluster">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => nav(-1)}
              className="text-white hover:bg-white/15 rounded-none h-9 px-3 border-0"
              data-testid="sds-editor-back-btn"
              title={t('common.back')}
            >
              <ArrowRight size={16} className={isEn ? 'rotate-180 mr-1' : 'ml-1'} />
              <span className="hidden sm:inline">{t('common.back')}</span>
            </Button>
            <div className="w-px h-5 bg-white/30"/>
            <Link to="/factory">
              <Button
                size="sm"
                variant="ghost"
                className="text-white hover:bg-white/15 rounded-none h-9 px-3 border-0"
                data-testid="sds-editor-home-btn"
                title={t('common.home')}
              >
                <Home size={16} className="ml-1" />
                <span className="hidden sm:inline">{t('common.home')}</span>
              </Button>
            </Link>
          </div>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={isEn ? 'Report title' : 'عنوان التقرير'}
            className="bg-white/10 border border-white/20 rounded px-3 py-1 text-sm placeholder-white/60 focus:outline-none focus:bg-white/20"
          />
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => window.dispatchEvent(new CustomEvent('sds:preview-pdf'))}
            className="bg-white/10 border-white/30 text-white hover:bg-white/20"
            data-testid="open-pdf-preview-btn"
          >
            <Eye size={14} className="ml-1" /> {t('sds.preview')}
          </Button>
          <Button
            size="sm"
            onClick={() => saveToBackend(false)}
            disabled={saving}
            className="bg-emerald-500 hover:bg-emerald-600 text-white"
            data-testid="save-pdf-btn"
          >
            {saving ? <Loader2 size={14} className="animate-spin ml-1" /> : <Save size={14} className="ml-1" />} {isEn ? 'Save' : 'حفظ'} PDF
          </Button>
        </div>
      </div>
      <AppEditor />
    </div>
  );
}
